import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Bell, Check, X, UserPlus, Video, Loader2 } from "lucide-react";

function initial(name = "") {
  return (name || "U").trim()[0]?.toUpperCase() || "U";
}

export function NotificationsBell() {
  const navigate = useNavigate();
  const [data, setData] = useState({ friend_requests: [], room_invites: [], count: 0 });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const live = useRef(true);

  const load = useCallback(async () => {
    try {
      const d = await api.socialNotifications();
      if (live.current) setData(d);
    } catch { /* ignore transient errors */ }
  }, []);

  useEffect(() => {
    live.current = true;
    load();
    const iv = setInterval(load, 20000);
    return () => { live.current = false; clearInterval(iv); };
  }, [load]);

  const acceptFriend = async (id) => {
    setBusy(id);
    try { await api.acceptFriendRequest(id); toast.success("Connection accepted."); await load(); }
    catch (e) { toast.error(e.message || "Could not accept"); }
    finally { setBusy(null); }
  };
  const declineFriend = async (id) => {
    setBusy(id);
    try { await api.declineFriendRequest(id); await load(); }
    catch (e) { toast.error(e.message || "Could not decline"); }
    finally { setBusy(null); }
  };
  const acceptInvite = async (inv) => {
    setBusy(inv.id);
    try {
      const res = await api.acceptRoomInvite(inv.id);
      setOpen(false);
      await load();
      toast.success("Joining the practice room…");
      navigate("/app/connect", { state: { openRoom: res.room_id } });
    } catch (e) {
      if (e.status === 402) { toast.error("Subscribe to join practice rooms."); navigate("/app/subscribe"); }
      else if (e.status === 410) { toast("That room has closed."); await load(); }
      else if (e.status === 409) { toast.error("That room is full."); await load(); }
      else toast.error(e.message || "Could not join");
    } finally { setBusy(null); }
  };
  const declineInvite = async (id) => {
    setBusy(id);
    try { await api.declineRoomInvite(id); await load(); }
    catch (e) { toast.error(e.message || "Could not decline"); }
    finally { setBusy(null); }
  };

  const count = data.count || 0;
  const hasItems = (data.friend_requests?.length || 0) + (data.room_invites?.length || 0) > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notifications-bell"
          className="relative flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground outline-none transition-colors hover:bg-secondary hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5" />
          {count > 0 && (
            <span
              data-testid="notifications-badge"
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground"
            >
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-panel">
        <div className="border-b border-border px-4 py-3">
          <p className="text-sm font-semibold">Notifications</p>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {!hasItems ? (
            <div className="px-3 py-8 text-center" data-testid="notifications-empty">
              <Bell className="mx-auto h-7 w-7 text-muted-foreground/60" />
              <p className="mt-2 text-sm text-muted-foreground">You're all caught up.</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {data.room_invites.map((inv) => (
                <div key={inv.id} className="rounded-lg border border-border p-3" data-testid={`notif-invite-${inv.id}`}>
                  <div className="flex items-start gap-2.5">
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground"><Video className="h-4 w-4" /></span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm"><span className="font-semibold">{inv.from_name}</span> invited you to practise{inv.title ? <> — <span className="text-muted-foreground">{inv.title}</span></> : ""}.</p>
                      <div className="mt-2 flex gap-2">
                        <Button size="sm" className="h-7" disabled={busy === inv.id} onClick={() => acceptInvite(inv)} data-testid={`accept-invite-${inv.id}`}>
                          {busy === inv.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Join"}
                        </Button>
                        <Button size="sm" variant="outline" className="h-7" disabled={busy === inv.id} onClick={() => declineInvite(inv.id)} data-testid={`decline-invite-${inv.id}`}>Dismiss</Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {data.friend_requests.map((r) => (
                <div key={r.id} className="rounded-lg border border-border p-3" data-testid={`notif-friend-${r.id}`}>
                  <div className="flex items-start gap-2.5">
                    <Avatar className="mt-0.5 h-8 w-8"><AvatarImage src={r.from_photo} /><AvatarFallback className="bg-primary/10 text-primary">{initial(r.from_name)}</AvatarFallback></Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm"><span className="font-semibold">{r.from_name}</span> wants to connect.</p>
                      <div className="mt-2 flex gap-2">
                        <Button size="sm" className="h-7" disabled={busy === r.id} onClick={() => acceptFriend(r.id)} data-testid={`accept-friend-${r.id}`}>
                          {busy === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Check className="mr-1 h-3.5 w-3.5" /> Accept</>}
                        </Button>
                        <Button size="sm" variant="outline" className="h-7" disabled={busy === r.id} onClick={() => declineFriend(r.id)} data-testid={`decline-friend-${r.id}`}><X className="mr-1 h-3.5 w-3.5" /> Decline</Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="border-t border-border p-2">
          <Button variant="ghost" size="sm" className="w-full justify-center" onClick={() => { setOpen(false); navigate("/app/friends"); }} data-testid="notifications-view-friends">
            <UserPlus className="mr-2 h-4 w-4" /> Manage friends
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
